import GlobalCollapsibleButton from './GlobalCollapsibleButton';
import { DeckIntelligenceScoreSummary } from './DeckIntelligenceScoreSummary';
import { MarkdownContent } from './MarkdownContent';
import { ViewSectionHeader } from './ViewPage';
import type { ClaimEvidence, DeckIntelligence, DeckIntelligenceCompetitor } from '../types';
import { formatBriefingProseForDisplay } from '../utils/formatBriefingProse';
import {
  aggregateRisk,
  claimCredibility,
  competitorFaviconUrl,
  formatScore,
  marketCrowding,
  scoreBand,
  scoreBandLabel,
  sectionPotential,
  SECTION_SCORE_KEYS,
  type ScoreDirection,
} from '../utils/deckIntelligenceScores';
import styles from './DeckIntelligenceSections.module.css';

const EXTRACTION_SECTIONS = SECTION_SCORE_KEYS;

const EVIDENCE_LABELS: Record<ClaimEvidence, string> = {
  supported: 'Supported',
  weak: 'Weak',
  likely_false: 'Likely false',
  speculative: 'Speculative',
};

const EVIDENCE_PILL_CLASS: Record<ClaimEvidence, string> = {
  supported: styles.evidencePillSupported,
  weak: styles.evidencePillWeak,
  likely_false: styles.evidencePillLikelyFalse,
  speculative: styles.evidencePillSpeculative,
};

const INVESTOR_MEMO_SECTIONS = [
  { key: 'bullCase' as const, label: 'Bull case' },
  { key: 'bearCase' as const, label: 'Bear case' },
  { key: 'devilsAdvocate' as const, label: "Devil's advocate" },
  { key: 'contrarianOpportunity' as const, label: 'Contrarian opportunity' },
];

type DeckIntelligenceSectionsProps = {
  intelligence: DeckIntelligence;
};

function NoneFlagged() {
  return <p className={`${styles.emptyState} muted`}>None flagged.</p>;
}

function SectionHeading({
  number,
  title,
  intro,
  score,
}: {
  number: number;
  title: string;
  intro: string;
  score?: React.ReactNode;
}) {
  return (
    <div className={styles.sectionHeadingWrap}>
      <ViewSectionHeader eyebrow={`Analysis ${number}`} title={title} subtitle={intro} />
      {score ? <div className={styles.sectionScorePanel}>{score}</div> : null}
    </div>
  );
}

const SCORE_BAND_CLASS: Record<ReturnType<typeof scoreBand>, string> = {
  strong: styles.scoreBandStrong,
  mixed: styles.scoreBandMixed,
  weak: styles.scoreBandWeak,
};

function ScoreSummaryPanel({
  value,
  max,
  label,
  hint,
  direction = 'higherIsBetter',
}: {
  value: number;
  max: number;
  label: string;
  hint: string;
  direction?: ScoreDirection;
}) {
  const band = scoreBand(value, max, direction);
  const fillPct = max === 0 ? 0 : Math.min(100, (value / max) * 100);
  const fillClass =
    direction === 'higherIsWorse'
      ? `${styles.scoreSummaryBarFill} ${styles.scoreSummaryBarFillWorse}`
      : styles.scoreSummaryBarFill;

  return (
    <div className={styles.scoreSummaryPanel}>
      <div className={styles.scoreSummaryHeader}>
        <p className={styles.scoreSummaryLabel}>{label}</p>
        <span className={`${styles.scoreSummaryBand} ${SCORE_BAND_CLASS[band]}`}>
          {scoreBandLabel(band, direction)}
        </span>
      </div>
      <p className={styles.scoreSummaryValue}>{formatScore(value, max)}</p>
      <div className={styles.scoreSummaryBarTrack} aria-hidden="true">
        <span className={fillClass} style={{ width: `${fillPct}%` }} />
      </div>
      <p className={`${styles.scoreSummaryHint} muted`}>{hint}</p>
    </div>
  );
}

function BriefingProse({ text }: { text: string }) {
  const trimmed = text.trim();

  return trimmed ? (
    <MarkdownContent
      content={formatBriefingProseForDisplay(trimmed)}
      className={styles.briefingMarkdown}
      tone="briefing"
    />
  ) : (
    <p className={`${styles.proseEmpty} muted`}>Not covered in this brief.</p>
  );
}

const SYNTHESIS_PARAGRAPH_FIELDS = [
  { key: 'mostDangerousAssumption' as const, label: 'Most dangerous assumption' },
  { key: 'hundredXRequirements' as const, label: '100× requirements' },
  { key: 'doubleDownEvidence' as const, label: 'Double-down evidence' },
];

function getRiskPillClass(evaluation: string): string {
  const normalized = evaluation.trim().toLowerCase();

  if (/\b(high|critical|severe)\b/.test(normalized)) {
    return styles.riskPillHigh;
  }
  if (/\b(medium|moderate)\b/.test(normalized)) {
    return styles.riskPillMedium;
  }
  if (/\b(low|minor)\b/.test(normalized)) {
    return styles.riskPillLow;
  }

  return styles.riskPillDefault;
}

function NumberedReasonsList({ items, emptyLabel }: { items: string[]; emptyLabel: string }) {
  const reasons = items.map((item) => item.trim()).filter(Boolean);

  if (reasons.length === 0) {
    return <p className={`${styles.emptyState} muted`}>{emptyLabel}</p>;
  }

  return (
    <ol className={styles.numberedReasonsList}>
      {reasons.map((reason, index) => (
        <li key={`${reason}-${index}`} className={styles.numberedReasonItem}>
          {reason}
        </li>
      ))}
    </ol>
  );
}

function CompetitorCard({ competitor }: { competitor: DeckIntelligenceCompetitor }) {
  const faviconUrl = competitorFaviconUrl(competitor.websiteUrl);
  const fillPct = (competitor.marketStrength / 10) * 100;

  const title = (
    <div className={styles.competitorTitleRow}>
      {faviconUrl ? (
        <img src={faviconUrl} alt="" width={20} height={20} className={styles.competitorFavicon} />
      ) : (
        <span className={styles.competitorFaviconFallback} aria-hidden="true" />
      )}
      <span className={styles.competitorName}>{competitor.name}</span>
      <span className={styles.competitorStrength}>{competitor.marketStrength}/10</span>
    </div>
  );

  return (
    <li className={styles.competitorCard}>
      {competitor.websiteUrl ? (
        <a
          href={competitor.websiteUrl}
          target="_blank"
          rel="noopener noreferrer"
          className={styles.competitorLink}
        >
          {title}
        </a>
      ) : (
        title
      )}
      <div className={styles.competitorStrengthBarTrack} aria-hidden="true">
        <span className={styles.competitorStrengthBarFill} style={{ width: `${fillPct}%` }} />
      </div>
      {competitor.note?.trim() ? (
        <p className={`${styles.competitorNote} muted`}>{competitor.note.trim()}</p>
      ) : null}
    </li>
  );
}

export function DeckIntelligenceSections({ intelligence }: DeckIntelligenceSectionsProps) {
  const { claims, risks, inversePitch, investorMemo, analysis, historicallySimilarCompanies } =
    intelligence;

  const potential = sectionPotential(intelligence.sectionScores);
  const credibility = claimCredibility(intelligence);
  const riskAggregate = aggregateRisk(intelligence);
  const crowding = marketCrowding(intelligence.competitors);
  const competitors = intelligence.competitors ?? [];
  const competitionSummary = intelligence.competition?.trim() ?? '';

  return (
    <>
      <DeckIntelligenceScoreSummary intelligence={intelligence} />

      <section aria-label="Deck breakdown" className={styles.extraction}>
        <SectionHeading
          number={1}
          title="Deck breakdown"
          intro="Key themes extracted from the pitch deck, scored by upside potential (1–10 per dimension)."
          score={
            potential ? (
              <ScoreSummaryPanel
                label="Potential sum"
                value={potential.total}
                max={potential.max}
                hint={`${formatScore(potential.average, 10)} average across ${EXTRACTION_SECTIONS.length} dimensions.`}
              />
            ) : undefined
          }
        />
        {EXTRACTION_SECTIONS.map(({ key, label }) => {
          const text = intelligence[key]?.trim() ?? '';
          const score = intelligence.sectionScores?.[key];

          return (
            <GlobalCollapsibleButton
              key={key}
              title={label}
              trailing={score != null ? `${score}/10` : undefined}
              size="comfortable"
            >
              {text ? (
                <MarkdownContent
                  content={formatBriefingProseForDisplay(text)}
                  className={styles.briefingMarkdown}
                  tone="briefing"
                />
              ) : (
                <p className={`${styles.proseEmpty} muted`}>No content extracted.</p>
              )}
            </GlobalCollapsibleButton>
          );
        })}
      </section>

      <section aria-label="Competition" className={styles.competitionSection}>
        <SectionHeading
          number={2}
          title="Competition"
          intro="Named competitors scored by market strength — higher totals mean a more crowded or dangerous field."
          score={
            crowding.count > 0 ? (
              <ScoreSummaryPanel
                label="Market pressure"
                value={crowding.totalStrength}
                max={crowding.maxStrength}
                direction="higherIsWorse"
                hint={`${crowding.count} competitors · ${formatScore(crowding.averageStrength, 10)} average strength.`}
              />
            ) : undefined
          }
        />
        {competitors.length > 0 ? (
          <ul className={styles.competitorsList}>
            {competitors.map((competitor, index) => (
              <CompetitorCard key={`${competitor.name}-${index}`} competitor={competitor} />
            ))}
          </ul>
        ) : competitionSummary ? (
          <BriefingProse text={competitionSummary} />
        ) : (
          <NoneFlagged />
        )}
        {competitors.length > 0 && competitionSummary ? (
          <div className={styles.competitionSummary}>
            <p className={styles.competitionSummaryLabel}>Landscape summary</p>
            <BriefingProse text={competitionSummary} />
          </div>
        ) : null}
      </section>

      <section aria-label="Claims" className={styles.verificationSection}>
        <SectionHeading
          number={3}
          title="Claims"
          intro="Assertions in the deck and how strongly the materials support them."
          score={
            claims.length > 0 ? (
              <ScoreSummaryPanel
                label="Claim credibility"
                value={credibility.average}
                max={10}
                hint={`${Math.round(credibility.bullshitRatio * 100)}% weak or likely false · lower = more bullshit.`}
              />
            ) : undefined
          }
        />
        {claims.length === 0 ? (
          <NoneFlagged />
        ) : (
          <ul className={styles.claimsList}>
            {claims.map((claim, index) => (
              <li key={`${claim.name}-${index}`} className={styles.claimItem}>
                <span className={styles.claimName}>{claim.name}</span>
                <span
                  className={`${styles.evidencePill} ${EVIDENCE_PILL_CLASS[claim.evidenceEvaluation]}`}
                >
                  {EVIDENCE_LABELS[claim.evidenceEvaluation]}
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section aria-label="Risks" className={styles.verificationSection}>
        <SectionHeading
          number={4}
          title="Risks"
          intro="Failure modes and concerns flagged during analysis."
          score={
            risks.length > 0 ? (
              <ScoreSummaryPanel
                label="Aggregate risk"
                value={riskAggregate.average}
                max={10}
                direction="higherIsWorse"
                hint={`Peak risk ${riskAggregate.peak}/10 across ${risks.length} flagged items.`}
              />
            ) : undefined
          }
        />
        {risks.length === 0 ? (
          <NoneFlagged />
        ) : (
          <ul className={styles.risksList}>
            {risks.map((risk, index) => (
              <li key={`${risk.description}-${index}`} className={styles.riskItem}>
                <p className={styles.riskDescription}>{risk.description}</p>
                <span
                  className={`${styles.evidencePill} ${getRiskPillClass(risk.riskEvaluation)}`}
                >
                  {risk.riskEvaluation}
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section aria-label="Inverse pitch" className={styles.perspectivesSection}>
        <SectionHeading
          number={5}
          title="Inverse pitch"
          intro="Stress-test the deck from both sides — how this deal dies and how it could win big."
        />
        <div className={styles.inversePitchGrid}>
          <div className={styles.inversePitchColumn}>
            <h3 className={styles.inversePitchColumnTitle}>Failure</h3>
            <div className={styles.inversePitchField}>
              <p className={styles.inversePitchFieldLabel}>Why it fails</p>
              <BriefingProse text={inversePitch.whyFailure} />
            </div>
            <div className={styles.inversePitchField}>
              <p className={styles.inversePitchFieldLabel}>Failure mechanism</p>
              <BriefingProse text={inversePitch.failureReason} />
            </div>
          </div>
          <div className={styles.inversePitchColumn}>
            <h3 className={styles.inversePitchColumnTitle}>Success</h3>
            <div className={styles.inversePitchField}>
              <p className={styles.inversePitchFieldLabel}>Why it wins</p>
              <BriefingProse text={inversePitch.whySuccess} />
            </div>
            <div className={styles.inversePitchField}>
              <p className={styles.inversePitchFieldLabel}>Success mechanism</p>
              <BriefingProse text={inversePitch.successReason} />
            </div>
          </div>
        </div>
      </section>

      <section aria-label="Investor memo" className={styles.perspectivesSection}>
        <SectionHeading
          number={6}
          title="Investor memo"
          intro="Internal briefing notes — bull, bear, and the angles worth pressure-testing in diligence."
        />
        <div className={styles.investorMemo}>
          {INVESTOR_MEMO_SECTIONS.map(({ key, label }) => (
            <GlobalCollapsibleButton key={key} title={label} size="comfortable">
              <BriefingProse text={investorMemo[key]} />
            </GlobalCollapsibleButton>
          ))}
        </div>
      </section>

      <section aria-label="Synthesis" className={styles.synthesisSection}>
        <SectionHeading
          number={7}
          title="Synthesis"
          intro="Outcome framing — what could make this a generational win or a zero, and what to verify next."
        />
        <div className={styles.synthesisReasonsGrid}>
          <div className={styles.synthesisReasonsColumn}>
            <h3 className={styles.synthesisReasonsTitle}>Top 3 unicorn reasons</h3>
            <NumberedReasonsList
              items={analysis.top3UnicornReasons}
              emptyLabel="No upside drivers listed."
            />
          </div>
          <div className={styles.synthesisReasonsColumn}>
            <h3 className={styles.synthesisReasonsTitle}>Top 3 death reasons</h3>
            <NumberedReasonsList
              items={analysis.top3DeathReasons}
              emptyLabel="No failure modes listed."
            />
          </div>
        </div>
        <div className={styles.synthesisParagraphs}>
          {SYNTHESIS_PARAGRAPH_FIELDS.map(({ key, label }) => (
            <div key={key} className={styles.synthesisParagraphField}>
              <p className={styles.synthesisParagraphLabel}>{label}</p>
              <BriefingProse text={analysis[key]} />
            </div>
          ))}
        </div>
      </section>

      <section aria-label="Historically similar companies" className={styles.similarCompaniesSection}>
        <SectionHeading
          number={8}
          title="Historically similar companies"
          intro="Analogues from prior cycles — pattern match, not a prediction."
        />
        {historicallySimilarCompanies.length === 0 ? (
          <NoneFlagged />
        ) : (
          <ul className={styles.similarCompaniesList}>
            {historicallySimilarCompanies.map((company, index) => (
              <li
                key={`${company.name}-${index}`}
                className={styles.similarCompanyCard}
              >
                <p className={styles.similarCompanyName}>{company.name}</p>
                <p className={`${styles.similarCompanySimilarity} muted`}>{company.similarity}</p>
              </li>
            ))}
          </ul>
        )}
      </section>
    </>
  );
}
